import { useState } from "react";
import { FiCheck, FiX, FiLink } from "react-icons/fi";
import { Button } from "newui/components/Button";
import type { DeclineReason } from "../../../../types/clarification-review.type";
import { DeclineReasonModal } from "./DeclineReasonModal";

interface BatchActionsBarProps {
	selectedCount: number;
	onBatchAccept: () => void;
	onBatchDecline: (reason: string, category: DeclineReason) => void;
	onLinkDuplicates: () => void;
	onClearSelection: () => void;
	isProcessing: boolean;
}

export const BatchActionsBar: React.FC<BatchActionsBarProps> = ({
	selectedCount,
	onBatchAccept,
	onBatchDecline,
	onLinkDuplicates,
	onClearSelection,
	isProcessing,
}) => {
	const [showDeclineModal, setShowDeclineModal] = useState(false);

	if (selectedCount === 0) {
		return null;
	}

	const handleDeclineConfirm = (reason: string, category: DeclineReason) => {
		onBatchDecline(reason, category);
		setShowDeclineModal(false);
	};

	return (
		<>
			<div
				className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2"
				data-testid="batch-actions-bar"
			>
				<span className="text-sm font-medium text-blue-700">
					{selectedCount} selected
				</span>
				<Button
					btntype="primary"
					size="xs"
					onClick={onBatchAccept}
					disabled={isProcessing}
					data-testid="batch-accept-btn"
				>
					<FiCheck size={14} />
					Accept Selected
				</Button>
				<Button
					btntype="secondary"
					size="xs"
					onClick={() => setShowDeclineModal(true)}
					disabled={isProcessing}
					data-testid="batch-decline-btn"
				>
					<FiX size={14} />
					Decline Selected
				</Button>
				{selectedCount >= 2 && (
					<Button
						btntype="secondary"
						size="xs"
						onClick={onLinkDuplicates}
						disabled={isProcessing}
						data-testid="batch-duplicate-btn"
					>
						<FiLink size={14} />
						Link as Duplicates
					</Button>
				)}
				<button
					type="button"
					onClick={onClearSelection}
					className="ml-auto text-xs text-gray-500 hover:text-gray-700"
				>
					Clear selection
				</button>
			</div>

			<DeclineReasonModal
				isOpen={showDeclineModal}
				count={selectedCount}
				onClose={() => setShowDeclineModal(false)}
				onConfirm={handleDeclineConfirm}
				isLoading={isProcessing}
			/>
		</>
	);
};
